import React, { useState } from 'react';
import type { NodeProps } from 'reactflow';
import type { PeopleTravelEvent, PeopleTravelEventKind } from '../../../../types';
import { EVENT_KIND_LABELS } from '../../../../utils/peopleTravelCanvasGraph';

export type TravelEventData = {
  event: PeopleTravelEvent;
  readOnly?: boolean;
  onTitleChange?: (eventId: string, title: string) => void;
  onKindChange?: (eventId: string, kind: PeopleTravelEventKind) => void;
};

const KINDS: PeopleTravelEventKind[] = ['work', 'meeting', 'hotel', 'meal', 'other'];

const TravelEventNode: React.FC<NodeProps<TravelEventData>> = ({ data, selected }) => {
  const { event, readOnly, onTitleChange, onKindChange } = data;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(event.title);

  const commit = () => {
    setEditing(false);
    const next = draft.trim() || event.title;
    if (next !== event.title) onTitleChange?.(event.id, next);
  };

  return (
    <div className={`people-travel-event-card people-travel-event-card--${event.kind} ${selected ? 'is-selected' : ''}`}>
      <div className="people-travel-event-kind-row nodrag">
        <select
          className="people-travel-event-kind"
          value={event.kind}
          disabled={readOnly}
          onChange={e => onKindChange?.(event.id, e.target.value as PeopleTravelEventKind)}
        >
          {KINDS.map(kind => (
            <option key={kind} value={kind}>
              {EVENT_KIND_LABELS[kind]}
            </option>
          ))}
        </select>
      </div>
      {editing && !readOnly ? (
        <input
          className="people-travel-event-title-input nowheel nodrag"
          value={draft}
          autoFocus
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setDraft(event.title);
              setEditing(false);
            }
          }}
        />
      ) : (
        <button
          type="button"
          className="people-travel-event-title nodrag"
          onDoubleClick={() => {
            if (readOnly) return;
            setDraft(event.title);
            setEditing(true);
          }}
        >
          {event.title}
        </button>
      )}
      {(event.startAt || event.endAt) && (
        <div className="people-travel-event-when">
          {[event.startAt, event.endAt].filter(Boolean).join(' → ')}
        </div>
      )}
      {event.notes && <div className="people-travel-event-notes">{event.notes}</div>}
    </div>
  );
};

export default React.memo(TravelEventNode);
